import { useMutation } from '@apollo/client';
import type {
  AddChildMutation,
  AddChildMutationVariables,
  AddParentMutation,
  AddParentMutationVariables,
  AddPartnerMutation,
  AddPartnerMutationVariables,
  AddSiblingMutation,
  AddSiblingMutationVariables,
  EditPersonMutation,
  EditPersonMutationVariables,
  RemovePersonMutation,
  RemovePersonMutationVariables,
  PersonInput,
} from '~/graphql/gen/graphql';
import { GeneticSex } from '~/graphql/gen/graphql';
import { normalizeGeneticSex } from '../utils/geneticSex';
import type { GeneticSexFormValue } from '../utils/geneticSex';
import type { EditPersonInput } from '~/graphql/gen/graphql';
import {
  ADD_CHILD,
  ADD_PARENT,
  ADD_PARTNER,
  ADD_SIBLING,
  EDIT_PERSON,
  REMOVE_PERSON,
} from '~/graphql/mutations';
import { ME, GET_FAMILY_TREE } from '~/graphql/queries';

export interface PersonFormData {
  firstName: string;
  lastName?: string;
  geneticSex: GeneticSex | GeneticSexFormValue;
  birthDate?: Date | null;
  deathDate?: Date | null;
  profileImageUrl?: string | null;
}

const buildPersonInput = (personData: PersonFormData): PersonInput => {
  return {
    firstName: personData.firstName,
    lastName: personData.lastName || undefined,
    geneticSex: normalizeGeneticSex(personData.geneticSex),
    birthDate: personData.birthDate
      ? personData.birthDate.toISOString()
      : undefined,
    deathDate: personData.deathDate
      ? personData.deathDate.toISOString()
      : undefined,
    profileImageUrl: personData.profileImageUrl || undefined,
  };
};

export interface UseFamilyTreeMutationsResult {
  addChild: (
    personId: number,
    childData: PersonFormData,
    familyId?: number,
  ) => Promise<AddChildMutation['addChild']>;
  addParent: (
    personId: number,
    parentData: PersonFormData,
    familyId?: number,
  ) => Promise<AddParentMutation['addParent']>;
  addPartner: (
    personId: number,
    partnerData: PersonFormData,
    familyId?: number,
  ) => Promise<AddPartnerMutation['addPartner']>;
  addSibling: (
    personId: number,
    siblingData: PersonFormData,
    familyId?: number,
  ) => Promise<AddSiblingMutation['addSibling']>;
  editPerson: (
    input: EditPersonInput,
  ) => Promise<EditPersonMutation['editPerson']>;
  removePerson: (
    personId: number,
  ) => Promise<RemovePersonMutation['removePerson']>;
  loading: {
    addChild: boolean;
    addParent: boolean;
    addPartner: boolean;
    addSibling: boolean;
    editPerson: boolean;
    removePerson: boolean;
  };
  errors: {
    addChild: Error | null;
    addParent: Error | null;
    addPartner: Error | null;
    addSibling: Error | null;
    editPerson: Error | null;
    removePerson: Error | null;
  };
}

export const useFamilyTreeMutations = (): UseFamilyTreeMutationsResult => {
  const mutationOptions = {
    errorPolicy: 'all' as const,
  };

  const [addChildMutation, { loading: addChildLoading, error: addChildError }] =
    useMutation<AddChildMutation, AddChildMutationVariables>(ADD_CHILD, {
      ...mutationOptions,
      awaitRefetchQueries: true,
      refetchQueries: (result) => {
        // Also refetch GET_FAMILY_TREE for membership trees
        const familyTreeId = result.data?.addChild?.familyTreeId;
        if (familyTreeId) {
          return [
            { query: ME },
            {
              query: GET_FAMILY_TREE,
              variables: { familyTreeId },
            },
          ];
        }

        return [{ query: ME }];
      },
    });

  const [
    addParentMutation,
    { loading: addParentLoading, error: addParentError },
  ] = useMutation<AddParentMutation, AddParentMutationVariables>(ADD_PARENT, {
    ...mutationOptions,
    awaitRefetchQueries: true,
    refetchQueries: (result) => {
      // Also refetch GET_FAMILY_TREE for membership trees
      const familyTreeId = result.data?.addParent?.familyTreeId;
      if (familyTreeId) {
        return [
          { query: ME },
          {
            query: GET_FAMILY_TREE,
            variables: { familyTreeId },
          },
        ];
      }

      return [{ query: ME }];
    },
  });

  const [
    addPartnerMutation,
    { loading: addPartnerLoading, error: addPartnerError },
  ] = useMutation<AddPartnerMutation, AddPartnerMutationVariables>(
    ADD_PARTNER,
    {
      ...mutationOptions,
      awaitRefetchQueries: true,
      refetchQueries: (result) => {
        // Also refetch GET_FAMILY_TREE for membership trees
        const familyTreeId = result.data?.addPartner?.familyTreeId;
        if (familyTreeId) {
          return [
            { query: ME },
            {
              query: GET_FAMILY_TREE,
              variables: { familyTreeId },
            },
          ];
        }

        return [{ query: ME }];
      },
    },
  );

  const [
    addSiblingMutation,
    { loading: addSiblingLoading, error: addSiblingError },
  ] = useMutation<AddSiblingMutation, AddSiblingMutationVariables>(
    ADD_SIBLING,
    {
      ...mutationOptions,
      awaitRefetchQueries: true,
      refetchQueries: (result) => {
        // Also refetch GET_FAMILY_TREE for membership trees
        const familyTreeId = result.data?.addSibling?.familyTreeId;
        if (familyTreeId) {
          return [
            { query: ME },
            {
              query: GET_FAMILY_TREE,
              variables: { familyTreeId },
            },
          ];
        }

        return [{ query: ME }];
      },
    },
  );

  const [
    editPersonMutation,
    { loading: editPersonLoading, error: editPersonError },
  ] = useMutation<EditPersonMutation, EditPersonMutationVariables>(
    EDIT_PERSON,
    {
      ...mutationOptions,
      awaitRefetchQueries: true,
      refetchQueries: (result) => {
        // Also refetch GET_FAMILY_TREE for membership trees
        const familyTreeId = result.data?.editPerson?.familyTreeId;
        if (familyTreeId) {
          return [
            { query: ME },
            {
              query: GET_FAMILY_TREE,
              variables: { familyTreeId },
            },
          ];
        }

        return [{ query: ME }];
      },
    },
  );

  const [
    removePersonMutation,
    { loading: removePersonLoading, error: removePersonError },
  ] = useMutation<RemovePersonMutation, RemovePersonMutationVariables>(
    REMOVE_PERSON,
    {
      ...mutationOptions,
      refetchQueries: [{ query: ME }],
      update: (cache, { data }) => {
        if (data?.removePerson) {
          const deletedId = data.removePerson;

          cache.evict({
            id: cache.identify({ __typename: 'Person', id: deletedId }),
          });

          // Also evict GET_FAMILY_TREE to ensure membership trees update
          cache.evict({ fieldName: 'getFamilyTree' });

          cache.gc();
        }
      },
    },
  );

  const addChild = async (
    personId: number,
    childData: PersonFormData,
    familyId?: number,
  ): Promise<AddChildMutation['addChild']> => {
    const variables: AddChildMutationVariables = {
      input: {
        personId,
        childData: buildPersonInput(childData),
        familyId,
      },
    };

    const result = await addChildMutation({ variables });

    if (!result.data?.addChild) {
      throw new Error('Failed to add child');
    }

    return result.data.addChild;
  };

  const addParent = async (
    personId: number,
    parentData: PersonFormData,
    familyId?: number,
  ): Promise<AddParentMutation['addParent']> => {
    const variables: AddParentMutationVariables = {
      input: {
        personId,
        parentData: buildPersonInput(parentData),
        familyId,
      },
    };

    const result = await addParentMutation({ variables });

    if (!result.data?.addParent) {
      throw new Error('Failed to add parent');
    }

    return result.data.addParent;
  };

  const addPartner = async (
    personId: number,
    partnerData: PersonFormData,
    familyId?: number,
  ): Promise<AddPartnerMutation['addPartner']> => {
    const variables: AddPartnerMutationVariables = {
      input: {
        personId,
        partnerData: buildPersonInput(partnerData),
        familyId,
      },
    };

    const result = await addPartnerMutation({ variables });

    if (!result.data?.addPartner) {
      throw new Error('Failed to add partner');
    }

    return result.data.addPartner;
  };

  const addSibling = async (
    personId: number,
    siblingData: PersonFormData,
    familyId?: number,
  ): Promise<AddSiblingMutation['addSibling']> => {
    const variables: AddSiblingMutationVariables = {
      input: {
        personId,
        siblingData: buildPersonInput(siblingData),
        familyId,
      },
    };

    const result = await addSiblingMutation({ variables });

    if (!result.data?.addSibling) {
      throw new Error('Failed to add sibling');
    }

    return result.data.addSibling;
  };

  const editPerson = async (
    input: EditPersonInput,
  ): Promise<EditPersonMutation['editPerson']> => {
    const result = await editPersonMutation({ variables: { input } });

    if (!result.data?.editPerson) {
      throw new Error('Failed to edit person');
    }

    return result.data.editPerson;
  };

  const removePerson = async (
    personId: number,
  ): Promise<RemovePersonMutation['removePerson']> => {
    const variables: RemovePersonMutationVariables = {
      input: {
        personId,
      },
    };

    const result = await removePersonMutation({ variables });

    if (result.data?.removePerson == null) {
      throw new Error('Failed to remove person');
    }

    return result.data.removePerson;
  };

  return {
    addChild,
    addParent,
    addPartner,
    addSibling,
    editPerson,
    removePerson,
    loading: {
      addChild: addChildLoading,
      addParent: addParentLoading,
      addPartner: addPartnerLoading,
      addSibling: addSiblingLoading,
      editPerson: editPersonLoading,
      removePerson: removePersonLoading,
    },
    errors: {
      addChild: addChildError || null,
      addParent: addParentError || null,
      addPartner: addPartnerError || null,
      addSibling: addSiblingError || null,
      editPerson: editPersonError || null,
      removePerson: removePersonError || null,
    },
  };
};
